import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  adminGetEvents, adminGetEvent, adminCreateEvent, adminUpdateEvent, adminDeleteEvent,
  adminGetVenues, adminGetMcs,
} from '../services/api';
import Navbar from '../components/Navbar';
import AdminTabBar from '../components/AdminTabBar';
import EventModal from '../components/EventModal';

const toDateStr = (d) => {
  if (!d) return '';
  return String(d).slice(0, 10);
};

const formatEventDate = (d) => {
  if (!d) return '—';
  const [year, month, day] = toDateStr(d).split('-');
  return `${day}/${month}/${year}`;
};

function TypeBadge({ eventType }) {
  const map = {
    showcase:          { bg: '#0ea5e9', label: 'Showcase' },
    peoples_choice:    { bg: '#f59e0b', label: "People's Choice" },
    lyricists:         { bg: '#8b5cf6', label: '3 Lyricists' },
    songwriters_round: { bg: '#10b981', label: 'Songwriters' },
  };
  const t = map[eventType] || { bg: '#6b7280', label: eventType };
  return (
    <span style={{
      background: t.bg, color: '#fff', borderRadius: '4px',
      padding: '2px 8px', fontSize: '12px', fontWeight: 600, display: 'inline-block',
    }}>
      {t.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    planning:  { bg: '#fef9c3', color: '#854d0e', label: 'Planning' },
    confirmed: { bg: '#dcfce7', color: '#166534', label: 'Confirmed' },
    completed: { bg: '#f3f4f6', color: '#6b7280', label: 'Completed' },
  };
  const s = map[status] || { bg: '#f3f4f6', color: '#6b7280', label: status };
  return (
    <span style={{
      background: s.bg, color: s.color, borderRadius: '9999px',
      padding: '2px 10px', fontSize: '12px', fontWeight: 600, display: 'inline-block',
    }}>
      {s.label}
    </span>
  );
}

function downloadBallotPdf(eventId) {
  const token = localStorage.getItem('token');
  fetch(`/api/admin/events/${eventId}/ballot-pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then(r => r.blob())
    .then(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ballot-event-${eventId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    })
    .catch(() => alert('Failed to download ballot PDF.'));
}

function EventCard({ event, isPast, onEdit, onDelete, canDelete }) {
  return (
    <div className="a-card" style={{ marginBottom: '10px', padding: '14px 20px' }}>
      {/* Row 1: name + badge | status + buttons */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <span style={{ fontWeight: 700, fontSize: '15px', color: 'var(--navy)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {event.name}
          </span>
          <TypeBadge eventType={event.event_type} />
          <StatusBadge status={event.status} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }}>
          {(event.event_type === 'peoples_choice' || event.event_type === 'lyricists') && (
            <button
              className="a-btn a-btn--ghost a-btn--sm"
              style={{ color: '#8b5cf6', borderColor: '#8b5cf6' }}
              onClick={() => downloadBallotPdf(event.id)}
            >
              Ballot PDF
            </button>
          )}
          <Link
            to={`/admin/events/${event.id}`}
            className="a-btn a-btn--ghost a-btn--sm"
            style={{ color: 'var(--blue)', borderColor: 'var(--blue)' }}
          >
            Manage
          </Link>
          <button className="a-btn a-btn--ghost a-btn--sm" onClick={() => onEdit(event)}>
            Edit
          </button>
          {/* Booking managers do everything else with an event but cannot delete
              one - the server enforces that (routes/events.js adminOnly). Hiding
              the button keeps the screen honest; offering it and returning 403
              would be a worse experience than not offering it. */}
          {!isPast && canDelete && (
            <button
              className="a-btn a-btn--ghost a-btn--sm"
              style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
              onClick={() => onDelete(event)}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Row 2: meta left | performers right */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
        <div>
          <div style={{ fontSize: '13px', color: 'var(--text)' }}>{formatEventDate(event.event_date)}</div>
          <div style={{ fontSize: '13px', color: 'var(--muted)' }}>{event.venue_name || 'No venue'}</div>
          <div style={{ fontSize: '13px', color: 'var(--muted)' }}>MC: {event.mc_name || 'No MC'}</div>
        </div>
        {event.performer_names && (
          <div style={{
            fontSize: '12px', color: '#6b7280',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {event.performer_names}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminEventsPage() {
  const [events, setEvents]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [venues, setVenues]             = useState([]);
  const [mcs, setMcs]                   = useState([]);
  const [sortDir, setSortDir]           = useState(() => {
    try { return localStorage.getItem('eventsSortDir') || 'desc'; } catch { return 'desc'; }
  });
  const [showCreate, setShowCreate]     = useState(false);
  const [editEvent, setEditEvent]       = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  // Only admins may delete an event; booking managers do everything else.
  // Enforced server-side in routes/events.js - this just hides the button.
  const canDelete = (() => {
    try { return ["admin", "metaadmin"].includes(JSON.parse(localStorage.getItem("user") || "{}").role); }
    catch { return false; }
  })();
  const [saving, setSaving]             = useState(false);
  const [deleting, setDeleting]         = useState(false);
  const [modalError, setModalError]     = useState('');

  const load = useCallback(() => {
    setLoading(true);
    adminGetEvents({ sort: sortDir })
      .then(r => setEvents(Array.isArray(r.data) ? r.data : []))
      .catch(() => setError('Failed to load events.'))
      .finally(() => setLoading(false));
  }, [sortDir]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    adminGetVenues().then(r => setVenues(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    adminGetMcs().then(r => setMcs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const today = String(new Date().toLocaleDateString('sv-SE'));
  const cmp = (a, b) => sortDir === 'asc'
    ? toDateStr(a.event_date).localeCompare(toDateStr(b.event_date))
    : toDateStr(b.event_date).localeCompare(toDateStr(a.event_date));
  const upcoming = events.filter(e => toDateStr(e.event_date) >= today).sort(cmp);
  const past     = events.filter(e => toDateStr(e.event_date) <  today).sort(cmp);

  const handleCreate = async (form) => {
    setModalError('');
    setSaving(true);
    try {
      const res = await adminCreateEvent({
        ...form,
        venue_id:         form.venue_id ? parseInt(form.venue_id) : null,
        mc_id:            form.mc_id ? parseInt(form.mc_id) : null,
        lyricists_count:  parseInt(form.lyricists_count) || 3,
        lyrics_sets_count: parseInt(form.lyrics_sets_count) || 3,
      });
      setEvents(prev => [...prev, res.data]);
      setShowCreate(false);
    } catch (err) {
      setModalError(err?.response?.data?.error || 'Failed to create event.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (form) => {
    setModalError('');
    setSaving(true);
    try {
      const res = await adminUpdateEvent(editEvent.id, {
        ...form,
        venue_id:         form.venue_id ? parseInt(form.venue_id) : null,
        mc_id:            form.mc_id ? parseInt(form.mc_id) : null,
        lyricists_count:  parseInt(form.lyricists_count) || 3,
        lyrics_sets_count: parseInt(form.lyrics_sets_count) || 3,
      });
      setEvents(prev => prev.map(e => e.id === editEvent.id ? res.data : e));
      setEditEvent(null);
    } catch (err) {
      setModalError(err?.response?.data?.error || 'Failed to update event.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await adminDeleteEvent(deleteConfirm.id);
      setEvents(prev => prev.filter(e => e.id !== deleteConfirm.id));
      setDeleteConfirm(null);
    } catch {
      alert('Failed to delete event.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Navbar />
      <AdminTabBar />
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '2rem 1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Events</h1>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => setSortDir(d => {
                const nd = d === 'desc' ? 'asc' : 'desc';
                try { localStorage.setItem('eventsSortDir', nd); } catch {}
                return nd;
              })}
              style={{
                background: '#0ea5e9', color: '#fff', border: 'none',
                borderRadius: '6px', padding: '8px 16px', fontSize: '13px',
                fontWeight: 500, cursor: 'pointer',
              }}
            >
              {sortDir === 'desc' ? 'Latest First' : 'Soonest First'}
            </button>
            <button className="a-btn a-btn--navy" onClick={() => { setShowCreate(true); setModalError(''); }}>
              Create Event
            </button>
          </div>
        </div>

        {error && <div className="a-error" style={{ marginBottom: '1rem' }}>{error}</div>}

        {loading ? (
          <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '3rem' }}>Loading…</div>
        ) : (
          <>
            {/* Upcoming Events */}
            <div style={{ marginBottom: '2rem' }}>
              {upcoming.length === 0 ? (
                <div style={{
                  textAlign: 'center', color: 'var(--muted)', padding: '2rem',
                  background: '#fff', borderRadius: '8px', border: '1px solid #e5e7eb',
                }}>
                  No upcoming events. Create one to get started.
                </div>
              ) : upcoming.map(event => (
                <EventCard
                  key={event.id}
                  event={event}
                  isPast={false}
                  onEdit={e => { adminGetEvent(e.id).then(r => { setEditEvent(r.data); setModalError(''); }).catch(() => { setEditEvent(e); setModalError(''); }); }}
                  onDelete={setDeleteConfirm}
                  canDelete={canDelete}
                />
              ))}
            </div>

            {/* Past Events */}
            {past.length > 0 && (
              <div>
                <h2 style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.75rem' }}>
                  Past Events
                </h2>
                {past.map(event => (
                  <EventCard
                    key={event.id}
                    event={event}
                    isPast={true}
                    onEdit={e => { adminGetEvent(e.id).then(r => { setEditEvent(r.data); setModalError(''); }).catch(() => { setEditEvent(e); setModalError(''); }); }}
                    onDelete={setDeleteConfirm}
                  canDelete={canDelete}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <EventModal
          isEdit={false}
          event={null}
          venues={venues}
          mcs={mcs}
          onSave={handleCreate}
          onClose={() => setShowCreate(false)}
          saving={saving}
          error={modalError}
        />
      )}

      {/* Edit Modal */}
      {editEvent && (
        <EventModal
          isEdit={true}
          event={editEvent}
          venues={venues}
          mcs={mcs}
          onSave={handleUpdate}
          onClose={() => setEditEvent(null)}
          saving={saving}
          error={modalError}
        />
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="a-modal-backdrop">
          <div className="a-modal">
            <div className="a-modal__title">Delete Event</div>
            <p style={{ marginBottom: '1.25rem', color: 'var(--text)', fontSize: '0.9rem' }}>
              Delete <strong>{deleteConfirm.name}</strong>? This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button className="a-btn a-btn--ghost" onClick={() => setDeleteConfirm(null)} disabled={deleting}>
                Cancel
              </button>
              <button className="a-btn a-btn--danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
